/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState, useMemo} from 'react'

// OOTB Components
import {
    Box,
    Button,
    SimpleGrid,
    Stack,
    Text,
    Image,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Custom Components
import CountrySelectorModalCheckout from '../country-selector-modal-checkout'
import useAddressFields from './useAddressFields'
import Field from '../field'
import PhoneField from '../phone-field'

// OOTB Hooks
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

// Custom hooks
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useGoogleMapsApi, importGoogleMapsLibraries} from '@app/components/google-maps'
import RoyalMailAddressAutocomplete from './royal-mail-address-autocomplete'

// utils
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    transformStateCode,
    normalizeCountryCode,
    isRoyalMailAutocompleteCountry,
    isGoogleAutocompleteCountry
} from '../../utils/address-utils'

// Constants
import {SHIPPING_COUNTRY_CODES} from '../../constants'

// Third Parties
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'

/**
 * AddressFields is a React functional component that renders a form for capturing
 * address details. It is designed to be used within a checkout or user profile
 * context where address information is required. The component supports both
 * shipping and billing address configurations and integrates with Google Maps
 * Places API to provide address autocomplete functionality.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.form - The form object used to manage form state and values.
 * @param {string} [props.prefix=''] - An optional prefix for form field names.
 * @param {boolean} [props.isShippingAddress=false] - A flag indicating if the form is for a shipping address.
 * @param {boolean} [props.isBillingAddress=false] - A flag indicating if the form is for a billing address.
 *
 * @returns {JSX.Element} A JSX element representing the address form.
 *
 * The component utilizes several hooks and libraries:
 * - `useCurrentCustomer`: Retrieves the current customer data.
 * - `useShipToCountry`: Provides the country to which shipping is available.
 * - `useAddressFields`: Generates the necessary form fields for address input.
 * - `useIntl`: Provides internationalization support for formatting messages.
 * - `useRef` and `useEffect`: Manage focus and lifecycle events for accessibility.
 *
 * The Google Maps Places API is used to enhance the user experience by providing
 * address autocomplete functionality. The component initializes the API with a
 * loader and sets up an autocomplete instance on the address input field.
 *
 * The component also includes modal functionality to allow users to select a
 * different shipping destination if needed. This is managed through state and
 * event handlers for opening and closing the modal.
 */
const AddressFields = ({
    form,
    prefix = '',
    forceContextCountryCode = true,
    isShippingAddress = false,
    isBillingAddress = false
}) => {
    const {data: customer} = useCurrentCustomer()
    const {shipToCountry} = useShipToCountry()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const forcePostalCodeValidation = isBillingAddress
    const fields = useAddressFields({form, prefix, forcePostalCodeValidation})
    const intl = useIntl()
    const addressFormRef = useRef()

    // Google Autocomplete suggestions
    const googleSuggestionsRef = useRef()
    const [googleSuggestions, setGoogleSuggestions] = useState([])

    useEffect(() => {
        // Focus on the form when the component mounts for accessibility
        addressFormRef?.current?.focus()
    }, [])

    // "Reset" the countryCode field when shipToCountry.id changes in context from getShipToCountry()
    // React form limit: defaultValue is setting once
    useEffect(() => {
        // No force the context country when explicitly disabled
        // Use case: edit mode in account address book
        if (!forceContextCountryCode) return

        if (shipToCountry?.id) {
            if (form.getValues(`${prefix}countryCode`) !== shipToCountry?.id) {
                form.setValue(`${prefix}countryCode`, shipToCountry?.id)
            }
        }
    }, [shipToCountry?.id])

    const libraries = useMemo(() => ['places'], [])

    const {app: appConfig} = getConfig()

    // `region` controls the country used for Google Autocomplete suggestions.
    // IMPORTANT: CHECKOUT case
    // - On the billing step, users can change the country. `form.watch` ensures
    //   Google suggestions update immediately based on user choice.
    // - On the shipping step, countryCode is disabled and always equals shipToCountry.id.
    //   In both cases, the autocomplete region stays in sync with the selected country.
    const watchedCountry = form.watch(`${prefix}countryCode`)
    const addressCountryCode = normalizeCountryCode(watchedCountry || shipToCountry?.id || '')

    const isRoyalMailAddressNowEnabled = appConfig?.ROYAL_MAIL_ADDRESSNOW_ENABLED === true
    const royalMailAddressNowKeyType =
        isShippingAddress || isBillingAddress ? 'checkout' : 'accountAddressBook'

    const isRoyalMailAutocompleteActive =
        isRoyalMailAddressNowEnabled && isRoyalMailAutocompleteCountry(addressCountryCode)
    const isGoogleAutocompleteActive = isGoogleAutocompleteCountry(addressCountryCode, {
        isRoyalMailAutocompleteActive
    })

    const {isApiAndLibrariesLoaded: isGoogleMapsApiLoaded} = useGoogleMapsApi({
        libraries,
        region: addressCountryCode,
        enabled: isGoogleAutocompleteActive
    })

    // Clear Google suggestions when the country (region) changes
    useEffect(() => {
        form.setValue(`${prefix}c_fullAddress`, '')
        setGoogleSuggestions([])
    }, [addressCountryCode])

    /**
     * Populates address-related form fields based on the provided place object, which contains structured address components.
     *
     * This function extracts relevant address information such as street number, route, subpremise, postal code, city, state, and country
     * from the `place.addressComponents` array. It handles country-specific logic for determining the city and state fields, accommodating
     * variations in address formats for countries like the UK, Brazil, Egypt, and South Korea. The extracted values are then set into the form
     * using the `form.setValue` method for fields including 'c_fullAddress', 'address1', 'address2', 'postalCode', 'city', 'stateCode', and 'c_phoneCountry'.
     *
     * @param {Object} place - The place object containing address components, typically returned from an address autocomplete or geocoding API.
     * @param {Array} place.addressComponents - An array of address component objects, each with `types`, `longText`, and `shortText` properties.
     *
     * @returns {void}
     *
     * @example
     * // Given a place object from an autocomplete API, fill in the form's address fields:
     * fillInAddress(place);
     */
    const fillInAddress = (place) => {
        let address1 = ''
        let address2 = ''
        let postcode = ''
        let city = ''
        let state = ''
        let fullAddress = ''
        let country = place?.addressComponents.find(
            (component) => component.types[0] === 'country'
        )?.shortText

        if (place) {
            for (const component of place.addressComponents) {
                const componentType = component.types[0]

                switch (componentType) {
                    case 'street_number': {
                        address1 = `${component.longText}`
                        break
                    }
                    case 'subpremise': {
                        address2 = `${component.longText}`
                        break
                    }
                    case 'route': {
                        address1 += ` ${component.shortText}`
                        break
                    }
                    case 'postal_code': {
                        postcode = `${component.longText}`
                        break
                    }
                    case 'postal_town': {
                        if (country === 'UK' || country === 'GB') {
                            city = component.longText
                        }
                        break
                    }
                    case 'administrative_area_level_1': {
                        if (country === 'EG' || country === 'KR') {
                            city = component.shortText
                        }
                        state = transformStateCode(country, component.shortText)
                        break
                    }
                    case 'administrative_area_level_2': {
                        if (country === 'BR') {
                            city = component.longText
                        }
                        break
                    }
                    case 'locality':
                        if (!['UK', 'GB', 'EG', 'KR', 'BR'].includes(country)) {
                            city = component.longText
                        }
                        break
                }
            }
        }

        // if the address1 field is empty, set it to the searched phrase
        if (!address1) {
            // get address1 maxlength attribute
            const address1MaxLength =
                addressFormRef?.current?.querySelector('#address1').getAttribute('maxlength') || 35
            // truncate address1 to maxlength
            address1 = (place.formattedAddress || '')
                .substring(0, address1MaxLength)
                // remove any comma from the value as it breaks order import on BKN side
                .replaceAll(',', '')

            fullAddress = address1
        } else {
            fullAddress = `${address1} ${postcode} ${city}`
        }

        form.setValue(`${prefix}c_fullAddress`, fullAddress)
        form.setValue(`${prefix}address1`, address1)
        form.setValue(`${prefix}address2`, address2)
        form.setValue(`${prefix}postalCode`, postcode)
        form.setValue(`${prefix}city`, city)
        form.setValue(`${prefix}stateCode`, state)

        form.trigger()
    }

    useEffect(() => {
        if (!isGoogleAutocompleteActive || !isGoogleMapsApiLoaded) return

        let newestRequestId = 0
        const request = {
            input: '',
            region: addressCountryCode,
            includedRegionCodes: [addressCountryCode]
        }

        /**
         * Refreshes the session token for a Google Maps Places Autocomplete request.
         *
         * This function generates a new `AutocompleteSessionToken` using the Google Maps JavaScript API
         * and assigns it to the `sessionToken` property of the provided request object. This is typically
         * used to ensure that each autocomplete session is uniquely identified, which can improve
         * autocomplete results and billing accuracy.
         *
         * @param {Object} request - The request object to which the new session token will be attached.
         */
        function refreshToken(request) {
            // Create a new session token and add it to the request.
            request.sessionToken = new window.google.maps.places.AutocompleteSessionToken()
        }

        /**
         * Handles the autocomplete logic for address input fields by fetching and displaying
         * place suggestions as the user types. This function resets the suggestion list if the
         * input is empty, sends the current input value to the Google Places Autocomplete API,
         * and ensures only the latest request's results are rendered to avoid race conditions.
         * For each suggestion, it creates a clickable list item that, when selected, triggers
         * the `onPlaceSelected` callback with the chosen place's details.
         *
         * @async
         * @function makeAutocompleteRequest
         * @param {Event} inputEvent - The input event from the address field, containing the current value.
         * @returns {Promise<void>} Resolves after updating the suggestions list or if input is empty.
         * @throws {Error} Propagates any errors from the autocomplete API call.
         *
         * @example
         * <input onInput={makeAutocompleteRequest} />
         */
        async function makeAutocompleteRequest(inputEvent) {
            // Explicitly sync c_fullAddress form field with user input
            // It will be updated on onPlaceSelected if not empty inputEvent.target.value
            form.setValue(`${prefix}c_fullAddress`, inputEvent.target.value)

            // Reset elements and exit if an empty string is received.
            if (inputEvent.target.value == '') {
                setGoogleSuggestions([])
                return
            }

            // Add the latest char sequence to the request.
            request.input = inputEvent.target.value
            // To avoid race conditions, store the request ID and compare after the request.
            const requestId = ++newestRequestId

            // Fetch autocomplete suggestions and show them in a list.
            const {suggestions} =
                await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                    request
                )
            // If the request has been superseded by a newer request, do not render the output.
            if (requestId !== newestRequestId) return

            setGoogleSuggestions(suggestions)
        }

        /**
         * Initializes the Google Places Autocomplete service for an address input field.
         *
         * This asynchronous function loads the Google Places library, locates the address input
         * and results container elements within the form, and attaches an input event listener
         * to trigger autocomplete requests as the user types. It also refreshes the authentication
         * token required for requests. Errors during initialization are caught and logged to the console.
         *
         * @async
         * @function
         * @throws Will log an error to the console if the Google Places library fails to load or if
         *         any other initialization step fails.
         */
        async function initGooglePlaces() {
            try {
                // Ensure the 'places' library is loaded
                await importGoogleMapsLibraries(['places'])

                const addressInput = addressFormRef?.current?.querySelector('#c_fullAddress')
                if (!addressInput) {
                    return
                }

                addressInput.addEventListener('input', makeAutocompleteRequest)
                refreshToken(request)
            } catch (error) {
                console.error('Error initilizing google places service', error)
            }
        }

        initGooglePlaces()

        // Cleanup function to remove the input event listener from the address input field.
        // This ensures that the event listener is properly removed when the component unmounts
        // or when the effect is re-run, preventing potential memory leaks and ensuring that
        // no stale event listeners remain attached to the DOM element.
        return () => {
            const addressInput = addressFormRef?.current?.querySelector('#c_fullAddress')
            if (addressInput) {
                addressInput.removeEventListener('input', makeAutocompleteRequest)
            }
        }
    }, [isGoogleAutocompleteActive, isGoogleMapsApiLoaded, addressCountryCode])

    /**
     * Handles the event when a place is selected from address suggestions.
     *
     * This asynchronous function fetches the specified fields ('formattedAddress' and 'addressComponents')
     * from the selected place object, fills in the address form with the retrieved data, and clears
     * the suggestions list.
     *
     * @async
     * @param {Object} place - The selected place object, expected to have a fetchFields method.
     * @returns {Promise<void>} Resolves when the address is filled and suggestions are cleared.
     */
    const onPlaceSelected = async (place) => {
        await place.fetchFields({
            fields: ['formattedAddress', 'addressComponents']
        })
        fillInAddress(place)
        setGoogleSuggestions([])
    }

    const truncateAddressField = (value = '', maxLength = 35) => value.substring(0, maxLength)

    const fillInRoyalMailAddress = (address) => {
        const address1 = truncateAddressField(address.address1 || '', 35)
        const address2 = truncateAddressField(address.address2 || '', 35)
        const city = truncateAddressField(address.city || '', 35)
        const postalCode = truncateAddressField(address.postalCode || '', 35)
        const stateCode = truncateAddressField(address.stateCode || '', 35)

        const fullAddress = [address1, address2, postalCode, city].filter(Boolean).join(' ')

        form.setValue(`${prefix}c_fullAddress`, fullAddress)
        form.setValue(`${prefix}address1`, address1)
        form.setValue(`${prefix}address2`, address2)
        form.setValue(`${prefix}city`, city)
        form.setValue(`${prefix}postalCode`, postalCode)
        form.setValue(`${prefix}stateCode`, stateCode)

        form.trigger()
    }

    /**
     * Opens a modal dialog by setting the state variable `isModalOpen` to `true`.
     * This function is typically used to trigger the display of a modal component
     * within the application, allowing users to interact with additional content
     * or perform specific actions within the modal.
     */
    const openModal = () => {
        setIsModalOpen(true)
    }

    /**
     * Closes the modal by setting the modal's open state to false.
     *
     * This function updates the state variable `isModalOpen` to `false`,
     * effectively closing the modal. It is typically used as an event
     * handler for actions that require the modal to be closed, such as
     * clicking a close button or submitting a form.
     */
    const closeModal = () => {
        setIsModalOpen(false)
    }

    return (
        <Stack
            spacing={24}
            aria-label={intl.formatMessage({
                id: 'use_address_fields.label.address_form',
                defaultMessage: 'Address Form'
            })}
            tabIndex="0"
            ref={addressFormRef}
        >
            {isRoyalMailAutocompleteActive && (
                <RoyalMailAddressAutocomplete
                    countryCode={addressCountryCode}
                    enabled={isRoyalMailAutocompleteActive}
                    keyType={royalMailAddressNowKeyType}
                    onAddressSelected={fillInRoyalMailAddress}
                />
            )}

            {isGoogleAutocompleteActive && (
                <Box>
                    <Field {...fields.c_fullAddress} />
                    <VStack
                        className="googleplaces-suggestions"
                        alignItems="flex-start"
                        pt={5}
                        pb={12}
                    >
                        {googleSuggestions.length > 0 && (
                            <ul className="googleplaces-list" ref={googleSuggestionsRef}>
                                {googleSuggestions.map((suggestion, index) => (
                                    <li key={index} className="googleplaces-item">
                                        <button
                                            type="button"
                                            className="googleplaces-option"
                                            onClick={() =>
                                                onPlaceSelected(
                                                    suggestion.placePrediction.toPlace()
                                                )
                                            }
                                        >
                                            {suggestion.placePrediction.text.toString()}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <Image
                            width={59}
                            src={getAssetUrl('static/img/google_on_white.png')}
                            alt="Powered by Google"
                        />
                    </VStack>
                </Box>
            )}

            {isShippingAddress && (
                <Box>
                    <Field {...fields.countryCode} isDisabled defaultValue={shipToCountry.id} />
                    <Text variant="bodySmall" mt={8}>
                        <FormattedMessage
                            id="use_address_fields.message.shipping_address"
                            defaultMessage="You are on the {country} store, shipping is only available for this country."
                            values={{
                                country: SHIPPING_COUNTRY_CODES.find(
                                    (c) => c.value === shipToCountry.id
                                )?.label
                            }}
                        />
                    </Text>
                    <Button
                        variant="link"
                        onClick={() => {
                            openModal(true)
                        }}
                        mt={8}
                    >
                        <FormattedMessage
                            id="use_address_fields.message.shipping_address_link"
                            defaultMessage="Shipping to another destination?"
                        />
                    </Button>
                </Box>
            )}
            <SimpleGrid columns={[1, 1, 2]} gap={24}>
                <Field {...fields.firstName} />
                <Field {...fields.lastName} />
            </SimpleGrid>
            {!isShippingAddress && <Field {...fields.countryCode} />}
            <Field {...fields.address1} />
            <Field {...fields.address2} />
            <SimpleGrid columns={[1, 1, 2]} gap={24}>
                <Field {...fields.city} />
                <Field {...fields.stateCode} />
            </SimpleGrid>
            <SimpleGrid columns={[1, 1, 2]} gap={24}>
                <Field {...fields.postalCode} />
                <PhoneField
                    {...fields.phone}
                    {...((isBillingAddress || isShippingAddress) && {
                        phoneName: `${prefix}c_phoneCountry`
                    })}
                />
            </SimpleGrid>
            {customer.isRegistered && !isBillingAddress && <Field {...fields.preferred} />}
            {isShippingAddress && (
                <CountrySelectorModalCheckout isOpen={isModalOpen} onClose={closeModal} />
            )}
        </Stack>
    )
}

AddressFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Forces countryCode on mount to match context country (shipToCountry.id) */
    forceContextCountryCode: PropTypes.bool,

    /** Optional flag to indication if an address is a billing address */
    isBillingAddress: PropTypes.bool,

    /** Optional flag to indication if an address is a shipping address */
    isShippingAddress: PropTypes.bool
}

export default AddressFields
